// Sidebar layout for the cohort-aware Founder portal at /<slug>/founder/*.
// Brand label, footer chrome, and which sidebar items render all come from
// the cohort theme. Adding a new cohort = one row in cohort-themes.ts + one
// SQL insert into the cohorts table. Zero changes here.
//
// Order of rituals matches PRD §13.2 for the 1to10 cohort. Investor Demand
// Brief is intentionally NOT in the sidebar — it surfaces inside Command
// Center / Advisor Thread / Investor Readiness as context.
import { Link, useLocation } from "wouter";
import {
  Home, MessagesSquare, Gauge, FileEdit, FolderCheck, ShieldCheck,
  Target, Users, Eye, HelpCircle, LogOut, Settings, Sun, Moon,
} from "lucide-react";
import { useCohort, fp } from "@/lib/founder-cohort";
import type { RitualKey } from "@/config/cohort-themes";
import { useThemeMode } from "@/lib/use-theme-mode";

type NavItem = {
  ritual: RitualKey | "command_center";
  sub: string;
  label: string;
  icon: typeof Home;
  exact?: boolean;
};

const NAV: NavItem[] = [
  { ritual: "command_center", sub: "", label: "Command Center", icon: Home, exact: true },
  { ritual: "thread", sub: "thread", label: "Advisor Thread", icon: MessagesSquare },
  { ritual: "readiness", sub: "readiness", label: "Investor Readiness", icon: Gauge },
  { ritual: "updates", sub: "updates", label: "Updates", icon: FileEdit },
  { ritual: "proof", sub: "proof", label: "Proof", icon: FolderCheck },
  { ritual: "review", sub: "review", label: "Review Room", icon: ShieldCheck },
  { ritual: "missions", sub: "missions", label: "Market Missions", icon: Target },
  { ritual: "network", sub: "network", label: "Mentor Connect", icon: Users },
  { ritual: "preview", sub: "preview", label: "Investor Page Preview", icon: Eye },
  { ritual: "ask", sub: "ask", label: "Ask Favcy", icon: HelpCircle },
];

export default function Founder1to10Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { slug, theme } = useCohort();
  // Per-portal day/night toggle. Founders get dark by default to match the
  // premium investor/banker aesthetic; preference is persisted per browser
  // via the shared useThemeMode hook. The `data-founder-theme` attribute on
  // the root wrapper drives the light-mode CSS overrides in index.css that
  // remap the hardcoded dark utilities (bg-[#0A0A0A], text-white/N, etc.)
  // to readable light values.
  const { mode: themeMode, toggle: toggleTheme } = useThemeMode("founder", "dark");
  const ThemeIcon = themeMode === "dark" ? Sun : Moon;
  const founderName = localStorage.getItem("founder_name") || "Founder";
  const companyName = localStorage.getItem("founder_company_name") || "";
  const initials = founderName.split(" ").map(p => p[0]).slice(0, 2).join("").toUpperCase() || "F";

  const handleSignOut = () => {
    ["founder_token", "founder_id", "founder_name", "founder_company_id", "founder_company_name", "founder_must_change_password"]
      .forEach(k => localStorage.removeItem(k));
    window.location.replace(fp(slug, "login"));
  };

  // Hide nav items the cohort theme has gated off. Routes still resolve so
  // existing links don't 404, but the nav doesn't surface them.
  const visibleNav = NAV.filter(item =>
    item.ritual === "command_center" ? true : theme.rituals[item.ritual as RitualKey],
  );

  return (
    <div data-founder-theme={themeMode} className={/* ALLOW_HARDCODED_COLOR: dark anchor remapped by [data-founder-theme="light"] */ "flex min-h-screen w-full bg-[#0A0A0A] text-white flex-col md:flex-row"}>
      <aside className="w-full md:w-64 bg-black/40 border-r border-white/[0.08] flex-shrink-0 flex flex-col">
        <div className="p-5 border-b border-white/[0.08]">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-[#FF5C00] flex items-center justify-center">
              <span className="text-white font-bold text-sm">F</span>
            </div>
            <div>
              <h1 className="text-base font-bold tracking-tight">{theme.brandName}</h1>
              <p className="text-white/60 text-[9px] font-mono uppercase tracking-[0.2em] leading-none mt-0.5">{theme.programLabel}</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
          {visibleNav.map(item => {
            const Icon = item.icon;
            const href = fp(slug, item.sub);
            const isActive = item.exact ? location === href : location === href || location.startsWith(href + "/");
            return (
              <Link
                key={href}
                href={href}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#FF5C00]/15 text-[#FF5C00] border border-[#FF5C00]/30"
                    : "text-white/70 hover:text-white hover:bg-white/[0.05] border border-transparent"
                }`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="p-3 border-t border-white/[0.08] space-y-2">
          <Link
            href={fp(slug, "settings")}
            data-testid="nav-settings"
            className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-white/70 hover:text-white hover:bg-white/[0.05]"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Link>
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/[0.04]">
            <div className="w-8 h-8 rounded-full bg-[#FF5C00]/20 border border-[#FF5C00]/40 flex items-center justify-center text-xs font-bold text-[#FF5C00]">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold truncate">{founderName}</div>
              <div className="text-[10px] text-white/60 truncate">{companyName}</div>
            </div>
            <button
              onClick={toggleTheme}
              title={`Switch to ${themeMode === "dark" ? "day" : "night"} mode`}
              aria-label="Toggle theme"
              data-testid="button-toggle-theme"
              className="text-white/60 hover:text-white/80 mr-1"
            >
              <ThemeIcon className="w-4 h-4" />
            </button>
            <button
              onClick={handleSignOut}
              title="Sign out"
              data-testid="button-sign-out"
              className="text-white/60 hover:text-white/80"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        {children}
      </main>
    </div>
  );
}
